import type { ReactNode } from 'react';
import { Navigate, useLocation, useRouter } from '@tanstack/react-router';
import { useStore } from '@nanostores/react';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { billing } from '@/services/billing';
import { JourneysHandoffView } from '@/services/router/journeys-handoff-view';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';
import BroadcastsListPage from './broadcasts-list.page';
import { useBroadcastsListContext } from './list-context';
import { withoutDefaults } from './url-state';
import { BROADCASTS_TAB_ROUTES, broadcastsTabFromPathname, landingBroadcastsTab, type BroadcastsTab } from './tabs';

/**
 * Which app serves the Broadcasts list while the rollout flag decides.
 *
 * The page lives at `/broadcasts` and its two tabs beneath it; the page being replaced answers
 * at the same paths under `/journeys`, plus the pre-rename `newsletters` spelling of the
 * one-time sends tab. Flag on, the `journeys` URLs forward here; flag off, these hand back to
 * them, which is what makes the flag a rollback rather than a way to reach a broken page. The
 * two directions can't loop, since each fires in the opposite flag state.
 */

/** The `journeys` URL the embedded app serves for a migrated tab, or for the bare list. */
const LEGACY_ROUTES = {
  one_time_sends: '/workspaces/$workspaceId/journeys/broadcasts/one_time_sends',
  'api-triggered': '/workspaces/$workspaceId/journeys/broadcasts/api-triggered',
  index: '/workspaces/$workspaceId/journeys/broadcasts',
} as const;

/** The migrated URL a `journeys` URL forwards to, with the query and hash carried. */
function useMigratedBroadcastsHref(tab: BroadcastsTab | null): string | null {
  const router = useRouter();
  return useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({
        to: tab == null ? '/workspaces/$workspaceId/broadcasts' : BROADCASTS_TAB_ROUTES[tab],
        params: { workspaceId },
      }).href,
  );
}

/** The `journeys` twin of the migrated URL being shown, with the query and hash carried. */
function useLegacyBroadcastsHref(): string | null {
  const router = useRouter();
  const { pathname } = useLocation();
  const tab = broadcastsTabFromPathname(pathname);
  return useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: LEGACY_ROUTES[tab ?? 'index'], params: { workspaceId } }).href,
  );
}

/**
 * The migrated list, at `/broadcasts` and its two tabs. Flag on it serves the page; flag off it
 * hands the reader back to the `journeys` URL the embedded app serves.
 */
export function BroadcastsListRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.BroadcastsMigration);
  const legacyHref = useLegacyBroadcastsHref();
  // The page's reads go through `requireWorkspaceId`, which throws while the workspaces list
  // is still loading, so readiness waits on the resolved workspace.
  const workspace = useCurrentWorkspace();

  // Keyed on the workspace: switching workspaces keeps this route mounted, and the page's saved
  // tab searches hold another workspace's tag, goal, object type and topic ids. Ember's list route
  // clears the same state in `resetController` when its model changes.
  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <BroadcastsListPage key={workspace?.id} />
    </MigrationRouteView>
  );
}

type JourneysBroadcastsHandoffProps = {
  /** The migrated tab this URL forwards to, or `null` for the bare list. */
  tab: BroadcastsTab | null;
};

/**
 * A `journeys` Broadcasts URL. Flag off it renders the embedded app, which still serves it;
 * flag on it forwards to the migrated URL. The pre-rename `newsletters` spelling forwards
 * straight to the one-time sends tab.
 */
export function JourneysBroadcastsHandoff({ tab }: JourneysBroadcastsHandoffProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.BroadcastsMigration);
  const migratedHref = useMigratedBroadcastsHref(tab);

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}

/**
 * The bare `/broadcasts`, which is never a page of its own: it sends the reader to the tab
 * they were last on, with that tab's filters if they left it moments ago, and otherwise to one-
 * time sends. It replaces the history entry, so Back doesn't land here and bounce forward.
 */
export function BroadcastsIndexRedirect() {
  const workspace = useCurrentWorkspace();
  const isBuilder = useStore(billing.isBuilder);
  const { saved } = useBroadcastsListContext();
  const tab = landingBroadcastsTab({ isBuilder });
  const search = saved[tab];

  if (workspace == null) {
    return null;
  }

  return (
    <Navigate
      to={BROADCASTS_TAB_ROUTES[tab]}
      params={{ workspaceId: String(workspace.id) }}
      search={search == null ? {} : withoutDefaults(search)}
      replace
    />
  );
}

type ApiTriggeredTabGuardProps = {
  children: ReactNode;
};

/**
 * Builder accounts have no API-triggered broadcasts (the campaigns endpoint refuses them), so
 * their tab is hidden and a direct link to it lands on one-time sends instead.
 */
export function ApiTriggeredTabGuard({ children }: ApiTriggeredTabGuardProps) {
  const workspace = useCurrentWorkspace();
  const isBuilder = useStore(billing.isBuilder);

  if (!isBuilder) {
    return children;
  }
  if (workspace == null) {
    return null;
  }
  return <Navigate to={BROADCASTS_TAB_ROUTES.one_time_sends} params={{ workspaceId: String(workspace.id) }} replace />;
}
